import { CommonModule } from '@angular/common';
import { Component, Inject, inject, signal, Signal, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { CacheService } from '@app/_services/cache.service';
import { CalendarIntegrationStore } from '@app/_stores';
import { CalendarIntegrationService } from '@app/_services/features/calendar-integration.service';

import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { CalendarAccountFormComponent } from '../calendar-account-form/calendar-account-form.component';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from '@app/_services/alert.service';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { StorageService } from '@app/_services/storage.servive';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-calendar-connect',
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './calendar-connect.component.html',
  styleUrl: './calendar-connect.component.scss'
})

export class CalendarConnectComponent implements OnInit, OnDestroy {

  readonly calendarStore = inject(CalendarIntegrationStore);
  private readonly calendarService = inject(CalendarIntegrationService);
  unSubscribe = new Subject<void>();
  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    private dialogRef: MatDialogRef<CalendarConnectComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { type: 'google' | 'microsoft' },
    public dialog: MatDialog,
    public cc: CacheService,
    public alertService: AlertService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private modalService: ModalService
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  async connectGoogle() {
    const credentials = await this.calendarService.connectGoogle();
    if (credentials) {
      await this.handleCalendarCredentials(credentials);
    }
  }

  async connectMicrosoft() {
    const credentials = await this.calendarService.connectMicrosoft();
    if (credentials) {
      await this.handleCalendarCredentials(credentials);
    }
  }

  private async handleCalendarCredentials(credentials: any) {
    try {
      this.cc.isLoading.set(true);
      const calendarDetails = await this.calendarService.getCalendarDetails(credentials);
      const activeAccounts = this.calendarStore.activeAccounts();
      const existingCalendar = activeAccounts.find((account) => account.email === calendarDetails.email && account.calendarType === calendarDetails.calendarType);
      if (existingCalendar) {
        this.alertService.alert(this.ss.t('duplicateCalendarAccount_title'), this.ss.t('duplicateCalendarAccount_content'), this.ss.t('common_ok'));
        return;
      } else {
        this.openCalendarAccountForm(calendarDetails);
      }
    } catch (error) {
      console.error('Calendar connection failed:', error);
    } finally {
      this.cc.isLoading.set(false);
    }
  }

  openCalendarAccountForm(calendarAccountData: CalendarIntegration) {
    let setupCalendarDialog;
    if (this.cc.isNarrowScreen()) {
      setupCalendarDialog = this.modalService.openMainContent(CalendarAccountFormComponent,
        {
          mode: 'new',
          value: calendarAccountData,
        },
        { disableClose: true, autoFocus: false }
      );
    } else {
      setupCalendarDialog = this.modalService.openLevel1(CalendarAccountFormComponent,
        {
          mode: 'new',
          value: calendarAccountData,
        },
        { disableClose: true, autoFocus: false }
      );
    }

    return this.modalService.getClosedObservable(setupCalendarDialog).pipe(takeUntil(this.unSubscribe)).subscribe(result => {
      if (result) {
        this.dialogRef.close(result);
      } else {
        this.closeDialog();
      }
    });
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('calendarSetup', previousScreen);
      }
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.as.screenExitEvent('calendarSetup', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
